import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';

import { teamApis } from '../team.query';
import { teamStore } from '../team.store';

const MAX_DOMAINS = 3;

interface AllowedDomainsCardProps {
  teamId: string;
  domains: string[];
}

export const AllowedDomainsCard = observer(function AllowedDomainsCard({
  teamId,
  domains,
}: AllowedDomainsCardProps) {
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const atCap = domains.length >= MAX_DOMAINS;

  const handleAdd = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await teamApis.addAllowedDomain(teamId, trimmed);
      setInputValue('');
    } catch {
      /* toast surfaced by query layer */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <YStack className="w-full gap-3">
      <SettingsSection.GroupHeader>Allowed Domains</SettingsSection.GroupHeader>

      {!atCap && (
        <XStack className="gap-2 items-center w-full">
          <Input
            type="text"
            placeholder="Add domain (e.g., acme.com)"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="flex-1"
            disabled={submitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
          />
          <Button
            variant="outline"
            className="text-sm whitespace-nowrap"
            onClick={handleAdd}
            isLoading={submitting}
            disabled={submitting || !inputValue.trim()}
          >
            Add
          </Button>
        </XStack>
      )}

      <SettingsSection.Group>
        <SettingsSection.Item direction="vertical">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemDescription>
              Users signing up with these email domains can join the team
              directly. Up to {MAX_DOMAINS} domains.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>

          <YStack className="w-full gap-0.5">
            {domains.length === 0 && (
              <p className="text-xs text-foreground-muted py-1">
                No allowed domains configured
              </p>
            )}
            {domains.map((domain) => (
              <ListEntry
                key={domain}
                label={domain}
                isRemoving={teamStore.mutatingDomain === domain}
                onRemove={() => teamApis.removeAllowedDomain(teamId, domain)}
              />
            ))}
          </YStack>
        </SettingsSection.Item>
      </SettingsSection.Group>
    </YStack>
  );
});

interface ListEntryProps {
  label: string;
  isRemoving: boolean;
  onRemove: () => void;
}

function ListEntry({ label, isRemoving, onRemove }: ListEntryProps) {
  return (
    <XStack className="w-full items-center justify-between gap-2 py-1">
      <span className="text-sm truncate">{label}</span>
      <button
        onClick={onRemove}
        disabled={isRemoving}
        className="shrink-0 p-0.5 hover:bg-surface-hover rounded disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={`Remove ${label}`}
      >
        <Icons.cancel size={12} className="text-foreground-muted" />
      </button>
    </XStack>
  );
}
